// InstanceMaterializer
// Two responsibilities:
//   1. materialize(): build InstancedMesh objects for a (tile, hash)
//      using `libraryParts[lod]` as the geometry/material source.
//   2. swapLod(): change which LOD an existing InstancedMesh renders
//      by swapping its geometry pointer.
//
// userData on each InstancedMesh keeps the legacy contract:
//   - array of per-instance entries with { id, uniqueId, index, hash,
//     tileKey, disabled, transformation, ... }
//   - userData.snapshotMatrices : Float32Array — column-major matrices
//     in three.js native form. Source of truth for "what should the
//     matrix be if visible". Read by InstanceStateApplicator.
//   - userData.centeredMatrices : Float32Array — alias of
//     snapshotMatrices for backwards-compat with viewer code that
//     reads centeredMatrices directly.
//   - userData.baseColors : Float32Array — original instance colors.
//   - userData.currentLod : numeric LOD index currently bound.
//
// === MATRIX CONVENTION (C# → three.js) ===
//
// The C# pipeline writes per-instance matrices using
// System.Numerics.Matrix4x4 after passing through MatrixZUpToYUp
// (axis swap). The resulting bytes represent a Y-up world transform
// in row-vector convention, serialized row-by-row.
//
// Three.js Matrix4 uses column-vector convention internally, with
// column-major storage. The conversion from "row-vector matrix
// serialized row-major" to "column-vector matrix in column-major
// storage" is a single transpose:
//
//   tmp.fromArray(dm)   ← reads as column-major; result = M_row^T
//                          (because reading row-major bytes as
//                          column-major effectively transposes)
//   tmp.transpose()     ← undoes that, then transposes once more
//                          to convert row-vector → column-vector;
//                          net result = M_col, which is what
//                          three.js expects
//
// Equivalent reasoning: the byte at index 12 in a row-major-
// serialized row-vector matrix holds translation.x. The byte at
// index 12 in column-major-stored column-vector form ALSO holds
// translation.x. Reading as column-major (fromArray) gets that
// right. But the byte at index 1 in the row-major form holds
// rotation R[0,1] (off-diagonal, first row), while the byte at
// index 1 in column-major form holds R[1,0] (off-diagonal, second
// row). Those are different elements unless R is symmetric.
// Transposing after fromArray swaps these into the right places.
//
// EMPIRICAL CONFIRMATION: with fromArray+transpose, all rotations,
// scales, and translations render as valid affine transforms — no
// shearing, no triangulation artifacts, no broken composition.
// Without the transpose, instances with non-identity rotation get
// sheared/destroyed. The transpose is non-negotiable.
//
// === LEGACY transformation FIELD ===
//
// helpers/three.js#instanceShow reconstructs the matrix via
// matrix.set(m0, m1, ..., m15). Matrix4.set takes ROW-major args
// (first 4 = first row of the matrix in column-vector convention).
//
// We need m0..m15 to give the SAME column-vector matrix we just
// stored. tmp.elements is column-major. To produce a row-major arg
// list from column-major storage means transposing access — but
// since the post-transpose tmp ALREADY holds the column-vector
// matrix, we can just dump tmp.elements straight into m0..m15 in
// index order and it works:
//
//   tmp.elements[0..3] is column 0 of the column-vector matrix
//   matrix.set(elements[0], elements[1], elements[2], elements[3], ...)
//      reads them as row 0 of the matrix it builds
//   → builds a matrix whose row 0 IS column 0 of tmp
//   → which is the TRANSPOSE of tmp
//   → which is M_col^T = M_row
//
// Wait — that gives instanceShow a row-vector matrix, not the
// column-vector matrix it needs.
//
// Resolution: instanceShow's set() is called with the SAME bytes
// that make up M_col in three.js elements order. The set() call
// rebuilds those bytes as row-major. If we just feed it the
// elements array verbatim, it produces M_col^T. To get M_col back,
// we'd need to feed elements in transposed order.
//
// This is exactly what helpers/three.js#instanceShow expected from
// the LEGACY GLB pipeline: it expected transformation.m0..m15 to
// be the **column-vector matrix written row-major**, i.e. set()
// reconstructs the column-vector form. The legacy GLB exporter
// happened to write that layout.
//
// To match that contract: write transformation.m0..m15 = the
// row-major form of M_col. tmp.elements is column-major, so we
// transpose-access:
//   m0  = elements[0]  m1  = elements[4]  m2  = elements[8]   m3  = elements[12]
//   m4  = elements[1]  m5  = elements[5]  m6  = elements[9]   m7  = elements[13]
//   m8  = elements[2]  m9  = elements[6]  m10 = elements[10]  m11 = elements[14]
//   m12 = elements[3]  m13 = elements[7]  m14 = elements[11]  m15 = elements[15]

// === MATERIALIZATION CONTRACT (post-consolidation) ===
//
// materialize() no longer creates a NEW InstancedMesh per (tile, hash).
// Instead it acquires slots in a GlobalInstancePool — one shared
// InstancedMesh per fragment hash, lived across every tile that uses
// that geometry. The big win: draw calls drop from O(tiles × hashes)
// to O(unique hashes in scene).
//
// The return shape is preserved so the rest of the streaming pipeline
// — MapMeshesAdapter, InstanceStateApplicator, StreamingController —
// doesn't have to know about pools. Records look identical:
//
//   { uniqueId, id, index, mesh, fragments: [{ mesh, index }] }
//
// `mesh` is the pool's currently-active InstancedMesh. After a pool
// grow this reference becomes stale (the pool swaps to a new mesh),
// which is exactly the kind of failure mode pools cannot afford. The
// adapter detects the swap via the pool's onMeshSwapped callback (see
// MapMeshesAdapter) and refreshes its record's mesh field.
//
// === LOD ===
//
// Pools are bound to one LOD at construction time. Per-tile LOD swaps
// became meaningless with consolidation — a swap would change geometry
// for every other tile in the pool. We pick LOD 0 (fine) and stay
// there. If the user reports overdraw at distance, the fix is three
// parallel pools per hash with slot promotion between them; that's a
// separate change.

import {} from 'three';
import { coincidentKey } from './coincidentKey';

export default class InstanceMaterializer {
  /**
   * Acquire slots in the global pool for this (tile, hash) batch.
   *
   * @param {Object}   args
   * @param {Array}    args.libraryParts   3-element [lo, med, hi] LOD slots
   * @param {string}   args.tileKey        tile owning the slots
   * @param {string}   args.hash           fragment hash
   * @param {Array}    args.instances      sidecar entries [{ uniqueId, deltaMatrix, disabled, ... }, ...]
   * @param {number}   args.initialLod     unused now — pool fixes its LOD at construction
   * @param {Object}   args.poolRegistry   InstancePoolRegistry (required)
   * @param {Object?}  args.applicator     optional InstanceStateApplicator
   */
  static materialize({
    libraryParts, tileKey, hash, instances,
    initialLod = 0,
    poolRegistry,
    applicator = null,
  }) {
    if (!poolRegistry) {
      throw new Error('InstanceMaterializer.materialize: poolRegistry required (post-consolidation)');
    }
    const slot = libraryParts[initialLod];
    if (!slot) {
      return { fragments: [], records: new Map() };
    }

    const pool = poolRegistry.getOrCreate(hash, libraryParts, initialLod);
    const records = new Map();
    const claimedIndices = [];

    // Dedupe instances whose deltaMatrix coincides with another in this
    // same (tile, hash) batch. Two coincident instances of the SAME
    // geometry render to the exact same pixels — the picker can only
    // resolve one of them, and the unselected duplicate wins z-fighting
    // on a per-pixel basis, making selected items appear unhighlighted.
    // Quantizing to 4 decimals = 0.1 mm precision in our BIM space,
    // safely below anything the user can perceive.
    const seenTransforms = new Set();
    let dupeCount = 0;
    for (const entry of instances) {
      // Translation + diagonal, read from the right indices for row-major
      // (JSON) and column-major (binary / moved) rows - see ./coincidentKey.
      const key = coincidentKey(entry);
      if (seenTransforms.has(key)) { dupeCount++; continue; }
      seenTransforms.add(key);

      const index = pool.acquire(entry.uniqueId, entry, /*applicator (deferred)*/ null, tileKey);
      claimedIndices.push(index);

      records.set(entry.uniqueId, {
        mesh: pool.mesh,
        pool,                       // adapter uses this to release on unload + survive grow
        uniqueId: entry.uniqueId,
        id: entry.uniqueId,
        index,
        fragments: [{ mesh: pool.mesh, pool, index }],
      });
    }
    if (dupeCount > 0) {
      console.log('[materializer] skipped', dupeCount, 'duplicate transform(s) of hash', hash, 'in tile', tileKey,
        '(prevents z-fight that hides selection highlight)');
    }

    // Pool growth during this batch is rare (the registry pre-sizes
    // pools from manifest counts) but possible. If pool.acquire above
    // triggered _grow on, say, the 257th instance of a 256-slot pool,
    // every record we built up to that point holds a stale `mesh`
    // reference (the now-disposed pre-grow InstancedMesh). Sweep
    // pool.mesh into every record here so adapter.register stores
    // current-valid references no matter how many grows happened.
    for (const rec of records.values()) {
      rec.mesh = pool.mesh;
      for (const f of rec.fragments) f.mesh = pool.mesh;
    }

    // NOTE: applicator.applyAll is no longer called here. The
    // streaming controller runs a single post-register sweep (with
    // the adapter threaded through) AFTER adapter.register, which
    // enforces the single-active-instance invariant correctly.
    // Calling applyAll here too would double-write — and worse,
    // without the adapter it would re-write snapshot matrices to
    // cross-tile duplicate slots from PREVIOUS materialize calls,
    // un-hiding them until the next full-sweep.
    //
    // The `applicator` param is preserved on the signature for
    // backwards compatibility with any future caller that wants the
    // pre-register sweep, but the streaming pipeline doesn't use it.

    return {
      fragments: [pool.mesh],
      records,
      // _poolClaim now records WHICH LOD pool the slots live in. The
      // streaming controller stores this on the tile so _unload knows
      // which (hash, lod) pool to release from, and _applyLodSwap can
      // migrate by releasing from the old LOD pool and re-acquiring
      // in the target LOD pool.
      _poolClaim: { hash, indices: claimedIndices, lod: initialLod },
    };
  }

  // Pool LOD is fixed at construction (see top-of-file comment). A
  // per-tile swap would mutate the geometry of a SHARED InstancedMesh,
  // breaking every other tile that uses the same pool. swapLod is
  // preserved as a no-op so the streaming controller can keep calling
  // it without a branch — proper distance-based LOD needs three
  // parallel pools per hash with slot promotion, not a geometry swap.
  static swapLod(/* instancedMesh, libraryParts, newLod */) {
    return false;
  }
}